import axios from 'axios';
import moment from "moment";
import './ViewInstallation.scss';
import bigInt from "big-integer";
import QrScanner from 'qr-scanner';
import { useAuth } from 'context/useAuth';
import { AuditData } from 'types/AuditData';
import Button from 'components/Button/Button';
import { Timestamp, where } from 'firebase/firestore';
import { Installation } from 'types/Installation';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FormLayout } from 'layouts/FormLayout/FormLayout';
import { FormInput } from 'components/FormInput/FormInput';
import { useAppSettings } from 'context/appsettingsContext';
import { useCollection } from 'functions/hooks/useCollection';
import CircularProgress from '@mui/material/CircularProgress';
import { isMobile as reactIsMobile } from 'react-device-detect';
import { isMobile as vanillaIsMobile } from 'mobile-device-detect';
import useGetInstallations from 'functions/hooks/useGetInstallations';
import InstallationStepList from 'components/InstallationStepList/InstallationStepList';
import InstallationStepCard from 'components/InstallationStepCard/InstallationStepCard';
import { autoLockIsOn, getAutoLockDelaySeconds, updateAutoLock, trimLeadingZeros, adminUnlock, getDisplayDateFromTimestamp } from 'functions/deviceStatusUtils';
import { confirmAsync } from 'functions/notification';
import { debounceByKey } from 'functions/debounceByKey';

export default function ViewInstallation() {
  
  const navigate = useNavigate();
  const settings = useAppSettings();
  const { authorisedUser } = useAuth();
  const [ device, setDevice ] = useState<any>();
  const [ hasCamera, setHasCamera ] = useState(false);
  const devicesCollection = useCollection<any>('devices');
  const vehiclesCollection = useCollection<any>('vehicles');
  const [ qrScanner, setQrScanner ] = useState<QrScanner>();
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ isMobile ] = useState(vanillaIsMobile || reactIsMobile);
  const [ qrScannerVideo, setQrScannerVideo ] = useState<HTMLVideoElement>();
  const [ isMobileView, setIsMobileView ] = useState(window.innerWidth <= 768);
  const installationsCollection = useCollection<Installation>('installations');
  const [ isNewInstallation ] = useState(useParams().installationId === '@new');
  const onDeviceUpdate = useCallback(async (device: any) => setDevice(device), []);
  const vehicleDeviceChangesCollection = useCollection<any>('vehicleDeviceChanges');
  const [ installation, setInstallation ] = useState(new Installation(undefined, settings));
  const remotes = device?.ConfigurationStatusLog?.configuration?.RemotesConfiguration?.RemoteConfigurations ?? [];
  const { installations, loadingInstallations } = useGetInstallations("BY_ID", { installationId: useParams().installationId });

  const [deviceReplacement] = useState(() => {

    const raw = new URLSearchParams(window.location.search).get("replace");
    if (!raw) return { oldDeviceId: "", newDeviceId: "" };
    const [oldDeviceId, , newDeviceId] = raw.split("|");
    if (!oldDeviceId || !newDeviceId) return { oldDeviceId: "", newDeviceId: "" };

    return {
        oldDeviceId: oldDeviceId?.trim()?.padStart(10, '0') ?? "",
        newDeviceId: newDeviceId?.trim()?.padStart(10, '0') ?? ""
    };
  });
  
  useEffect(() => {
    document.title = installation.id ? `View Installation : ${installation.id} | Titan Secure` : `Add Installation | Titan Secure`;
  });

  useEffect(() => {
    
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };

  }, []);

  useEffect(() => {
    if (installations[0]) {
      setInstallation(installations[0]);
    }
  }, [installations]);

  useEffect(() => {
    (async () => setHasCamera(await QrScanner.hasCamera()))();
  }, [hasCamera]);

  useEffect(() => {
    if (!qrScannerVideo) {
      setQrScannerVideo(document.getElementById('qr-scanner') as HTMLVideoElement);
    }
  }, [hasCamera, isMobile, qrScannerVideo]);

  useEffect(() => {
    if (!qrScanner && qrScannerVideo) {
      (async () => {

        const scanner = new QrScanner(qrScannerVideo, result => {
          
          console.log('QR : ', result);
          const deviceIdData = trimLeadingZeros(`${result?.data}`.trim());
          
          if (deviceIdData) {
            setInstallation(installation => ({ ...installation, deviceId: deviceIdData }));
          }
          
        },
        {
          highlightScanRegion: true,
          highlightCodeOutline: true,
          returnDetailedScanResult: true
        });

        try {
          await scanner.start();
          setQrScanner(scanner);
        } catch (error) {
          console.error('Failed to start qrScanner : ', error);
        }
  
      })();
    }
    if (qrScanner?.destroy) {
      return function () {
        try {
          qrScanner.destroy();
        } catch (error) {
          console.error('Failed to destroy qrScanner', error);
        }
      }
    }
  }, [hasCamera, isMobile, qrScannerVideo, qrScanner]);

  useEffect(() => installation.deviceId ? devicesCollection.getOneStream(installation.deviceId, onDeviceUpdate) : undefined, [
    installation.deviceId, devicesCollection, onDeviceUpdate,
  ]);

  function scrollToBottom() {
    window.setTimeout(() => {
      const scroller = document.querySelector(".fixed-layout__main");
      scroller?.scrollTo({ top: scroller.scrollHeight, behavior: "smooth" });
    }, 500);
  }

  const enableAutoLockAndAdminUnlock = useCallback(async function() {
    
    if (installation.deviceId && installation.deviceId !== '@new') {
      
      if (settings.installationChecklistAutolockDisabled !== 'true') {
        try { await updateAutoLock(settings.updateDeviceAutoLockUrl, authorisedUser.accessToken, installation.deviceId, Number(settings.installationChecklistAutoLockDelaySeconds)); } catch(e) {}
      }

      try { await adminUnlock(settings.handleLockCommandsUrl, authorisedUser.accessToken, installation.deviceId); } catch(e) {}
    }
    
  }, [installation.deviceId, settings.installationChecklistAutolockDisabled, settings.updateDeviceAutoLockUrl, settings.installationChecklistAutoLockDelaySeconds, settings.handleLockCommandsUrl, authorisedUser.accessToken]);

  useEffect(() => {
    // ensure auto lock and admin-unlock on exit
    return () => {
      if (isNewInstallation && installation.dateStarted) {
        enableAutoLockAndAdminUnlock();
      }
    }
  }, [enableAutoLockAndAdminUnlock, installation.dateStarted, isNewInstallation]);

  async function start() {
    
    const deviceId = installation.deviceId!.padStart(10, '0');

    if (await confirmAsync(deviceId)) {
      
      try {

        setIsSubmitting(true);

        const response = await axios.post(
          settings.manualUpdateDeviceRegionUrl,
          JSON.stringify({ deviceId: deviceId }),
          { headers: { Authorization: `Bearer ${authorisedUser.accessToken}`, 'Content-Type': 'application/json' } }
        );
        
        if (response?.status !== 200) {
          throw new Error(response.data);
        }

        setInstallation(installation => ({ ...installation, deviceId: deviceId, dateStarted: Timestamp.now() }));

        try {
          qrScanner?.stop();
        } catch (error) {
          console.error('Failed to stop qrScanner : ', error);
        }

        try {
          qrScanner?.destroy();
        } catch (error) {
          console.error('Failed to destroy qrScanner : ', error);
        }

      } catch (error: any) {
        
        window.alert('Failed to map device to region : ' + (error.message || error));

      } finally {

        setIsSubmitting(false);

      }
    }
  }

  const update = useCallback(async function() {

    // update in memory

    setInstallation({ ...installation });

    // current step

    const currentStep = installation.steps.find(x => !x.dateVerified && !x.dateFailed);
    
    if (currentStep) {
      
      if (!currentStep.dateStarted) {
        currentStep.dateStarted = Timestamp.now();
        setInstallation({ ...installation });
        scrollToBottom();
      }

    } else if (!installation.id && await debounceByKey({ key: `save-installation/${installation.deviceId}`, duration: 10000 }).then(() => true).catch(() => false)) {

      try {

        setIsSubmitting(true);

        // id

        if (!installation.id) {
          const epochMillis = moment().valueOf();
          const randomNum = Math.floor(100 + Math.random() * 900);
          const combinedValue = `${epochMillis}${randomNum}`;
          installation.id = bigInt(combinedValue).toString(36).toUpperCase();
        }

        // audit
        
        if (!installation.audit) {
          installation.audit = new AuditData();
        }

        if (!installation.audit.createdBy) {
          installation.audit.createdBy = installation.audit.updatedBy = authorisedUser.userFullName;
        }

        if (!installation.dateCompleted) {
          installation.dateCompleted = installation.audit.createdAt = installation.audit.updatedAt = Timestamp.now();
        }
        
        // failure

        const failedStep = installation.steps.find(x => x.dateFailed);

        if (failedStep && !installation.dateFailed) {
          installation.dateFailed = failedStep.dateFailed;
          installation.failDescription = `${failedStep.name} : ${failedStep.failDescription}`;
        }

        // save

        await installationsCollection.insertOrUpdate(installation.id, JSON.parse(JSON.stringify(installation)));

        // device replacement

        if (deviceReplacement.newDeviceId && deviceReplacement.oldDeviceId) {
            const vehicle = await vehiclesCollection.queryOne([where("deviceId", "==", deviceReplacement.oldDeviceId)]);
            if (vehicle) await vehiclesCollection.insertOrUpdate(vehicle.id, { deviceId: deviceReplacement.newDeviceId });
            await vehicleDeviceChangesCollection.insertOne({ type: 'link', vehicleId: vehicle?.id, oldDeviceId: deviceReplacement.oldDeviceId, newDeviceId: deviceReplacement.newDeviceId, timestamp: Timestamp.now(), byUserId: authorisedUser.userId, byUserFullName: authorisedUser.userFullName, installationId: installation.id });
            installation.deviceReplaced = true;
        }

        // update state

        setInstallation({ ...installation });
        
      } catch (error: any) {
        
        window.alert("Failed to save : " + (error.message || error));

      } finally {
        
        setIsSubmitting(false);

        // ensure auto-lock and admin-unlock is enabled

        try {
          await enableAutoLockAndAdminUnlock();
        } catch(error: any) {
          window.alert("Failed to enable Auto-Lock : " + (error.message || error));
        }

      }
      
    }

  }, [installation, installationsCollection, deviceReplacement.newDeviceId, deviceReplacement.oldDeviceId, authorisedUser.userFullName, authorisedUser.userId, vehiclesCollection, vehicleDeviceChangesCollection, enableAutoLockAndAdminUnlock]);

  if (!installation.deviceId && deviceReplacement.newDeviceId) {
    installation.deviceId = deviceReplacement.newDeviceId;
  }

  return (
    <FormLayout>
      
      <FormLayout.Header>
        <FormLayout.HeaderBack onClose={() => navigate('/agent/installations/all')} />
        <h2 style={{ fontSize: 'larger' }}>
          {installation.id ? `Installation : ${installation.id}` : `Installation`}
        </h2>
        <p style={{ fontSize: 'smaller', display: installation.dateStarted ? 'block' : 'none' }}>
          <span style={{ display: 'block' }}>
            <span style={{ whiteSpace: 'nowrap' }}>Device ID : {installation.deviceId}</span><>&nbsp;&nbsp;</>
            <span style={{ whiteSpace: 'nowrap' }}>Auto-Lock : { autoLockIsOn(device) ? `Enabled (${getAutoLockDelaySeconds(device)} seconds)` : 'Disabled' }</span>
          </span>
          <span style={{ display: 'block' }}>
            Remotes : {remotes.map((remote: any, index: number) => (
              <span key={remote.RemoteSerialNumber} style={{ whiteSpace: 'nowrap' }}>
                {remote.RemoteSerialNumber}{remote.IsPanic ? '(P)' : ''}{index < remotes.length - 1 ? <>&nbsp;&nbsp;</> : ''}
              </span>
            ))}
          </span>
        </p>
      </FormLayout.Header>
      
      <FormLayout.Main isNotNarrow={isMobileView}>
        
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {loadingInstallations && (
            <div className='installation__loader'>
              <CircularProgress />
            </div>
          )}
        </div>

        <div style={{ display: !installation.dateStarted && qrScanner ? 'flex' : 'none', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginTop: '20px'  }}>
          <video id="qr-scanner" style={{ width: '300px', 'height': '300px' }} width={300} height={300}></video>
        </div>

        <div style={{ display: !installation.dateStarted ? 'flex' : 'none', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px'  }}>
          {deviceReplacement.oldDeviceId && (
            <div style={{ marginBottom: "10px" }}>
                <FormInput
                id='OldDeviceId'
                label='Old Device ID'
                type='number'
                min={0}
                maxLength={10}
                max={9999999999}
                width='200px'
                value={deviceReplacement.oldDeviceId || ''}
                disabled={true}
                />
            </div>
          )}
          <FormInput
            id='DeviceId'
            label={deviceReplacement.newDeviceId ? 'New Device ID' : 'Device ID'}
            type='number'
            min={0}
            maxLength={10}
            max={9999999999}
            width={'200px'}
            value={installation.deviceId || ''}
            disabled={!!deviceReplacement.newDeviceId}
            onInput={e => {
              const value = (e.target as any).value.trim().replace(/[^0-9]/g, '').replace(/[eE+\-.]/g, '');
              if (value[0] !== '0' && value.length <= 10) setInstallation(installation => ({ ...installation, deviceId: value}));
            }}
            />
          <Button
            label={'\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0Start\u00A0\u00A0\u00A0\u00A0\u00A0'}
            onClick={start}
            style={{ flexShrink: 0 }}
            disabled={!installation.deviceId || isSubmitting}
            />
        </div>
        
        <InstallationStepList>
          {!loadingInstallations && installation.deviceId && installation.dateStarted && (installation.steps || []).map((step, index) => (
            <InstallationStepCard key={step.code} index={index} installation={installation} step={step} update={update} />
          ))}
        </InstallationStepList>
        
      </FormLayout.Main>

      {installation.id && (
        <FormLayout.Footer>
          <div style={{ fontWeight: 'bolder', textAlign: 'center' }}>
            <div>
              Installation : {installation.id}
            </div>
            <div>
              { installation.dateFailed ? `Failed` : installation.dateCompleted ? `Completed` : '<Unknown Status>' } @ { installation.dateFailed ? getDisplayDateFromTimestamp(installation.dateFailed) : installation.dateCompleted ? getDisplayDateFromTimestamp(installation.dateCompleted) : '' }
            </div>
            {installation.deviceReplaced && (
              <div>
                Device replaced
              </div>
            )}
          </div>
        </FormLayout.Footer>
      )}
      
    </FormLayout>
  );
}